import { markerBadges, railMarkerBadges, reasonLines, verdictTone } from "./regmFormat";

const UNAVAILABLE = "Unavailable";

export const filterRecords = (
  records,
  {
    flaggedOnly = false,
    query = "",
    hasShortSales = false,
    hasLongSales = false,
    netShortToday = false,
    unresolved = false,
    pendingReview = false,
  } = {},
) => {
  const needle = String(query || "").trim().toLowerCase();
  return (Array.isArray(records) ? records : []).filter((r) => {
    if (!r) return false;
    if (flaggedOnly && r.flagged !== true) return false;
    if (hasShortSales && r.has_short_sales !== true) return false;
    if (hasLongSales && r.has_long_sales !== true) return false;
    if (netShortToday && r.net_short_today !== true) return false;
    if (unresolved && r.unresolved_security !== true) return false;
    if (pendingReview && r.review_status !== "pending") return false;
    if (!needle) return true;
    return [r.security, r.isin, r.user].some(
      (v) => v != null && String(v).toLowerCase().includes(needle),
    );
  });
};

export const formatAttestedAt = (iso) => {
  const date = new Date(iso);
  // A timestamp with no Z or offset would be read as local time, so it is not trusted.
  if (typeof iso !== "string" || !/(Z|[+-]\d{2}:?\d{2})$/i.test(iso)) return UNAVAILABLE;
  if (!iso || Number.isNaN(date.getTime())) return UNAVAILABLE;
  return `${date.toISOString().slice(0, 19).replace("T", " ")} UTC`;
};

export const paginate = (records, page, rowsPerPage) =>
  (Array.isArray(records) ? records : []).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

// VERIFIED only when all three flags are exactly true; anything else is never shown as verified.
export const verificationLabel = (result) => {
  const verified = result?.verified;
  const chainOk = result?.chain_ok;
  const reviewsOk = result?.reviews_ok;
  if (verified === true && chainOk === true && reviewsOk === true) return "VERIFIED";
  if (chainOk === false) return "NOT VERIFIED (chain broken)";
  if (reviewsOk === false) return "NOT VERIFIED (REVIEW CHAIN PROBLEM)";
  return "NOT VERIFIED";
};

export const reviewProblems = (result) =>
  Array.isArray(result?.review_problems)
    ? result.review_problems.filter((p) => typeof p === "string")
    : [];

export const assertRecordList = (data) => {
  if (!Array.isArray(data)) throw new Error("Unexpected response from server");
  return data;
};

export const clampPage = (page, total, rowsPerPage) =>
  Math.min(page, Math.max(0, Math.ceil(total / rowsPerPage) - 1));

const REVIEW_LABELS = {
  pending: "Pending review",
  cleared: "Cleared",
  not_applicable: "Not applicable",
};

// Known strings only (own keys, exact case); anything else is never shown as Cleared.
export const reviewStatusLabel = (status) =>
  typeof status === "string" && Object.prototype.hasOwnProperty.call(REVIEW_LABELS, status)
    ? REVIEW_LABELS[status]
    : UNAVAILABLE;

export const REVIEW_ROLE = "role.compliance";

// Convenience only: the server decides who may review.
export const reviewAction = (roles, record) => {
  if (!Array.isArray(roles) || !roles.includes(REVIEW_ROLE)) return null;
  if (!record || record.flagged !== true) return null;
  if (record.review_status === "pending") return "clear";
  if (record.review_status === "cleared") return "reopen";
  return null;
};

export const REVIEW_NOTE_MIN = 10;
export const REVIEW_NOTE_MAX = 2000;

export const validateReviewNote = (note) => {
  // Code points, matching the backend's Python len.
  const length = typeof note === "string" ? [...note.trim()].length : 0;
  let message = "";
  if (length < REVIEW_NOTE_MIN) message = `Note must be at least ${REVIEW_NOTE_MIN} characters.`;
  else if (length > REVIEW_NOTE_MAX) message = `Note must be at most ${REVIEW_NOTE_MAX} characters.`;
  return { valid: message === "", length, message };
};

export const reviewedByLine = (record) => {
  const by = record?.reviewed_by;
  if (typeof by !== "string" || !by) return "";
  const at = formatAttestedAt(record.reviewed_at);
  return at === UNAVAILABLE ? by : `${by}, ${at}`;
};

// Quick chips first, then the four marker filters shown in the "More filters" menu.
export const MORE_FILTERS = [
  ["hasShortSales", "Short sales"],
  ["hasLongSales", "Sales of long positions"],
  ["netShortToday", "Net short today"],
  ["unresolved", "Unresolved"],
];

const FILTER_KEYS = ["flaggedOnly", "pendingReview", ...MORE_FILTERS.map(([k]) => k)];

// Compliance starts on its work queue ("Pending review"); everyone else sees every record.
export const defaultFilters = (roles) => ({
  flaggedOnly: false,
  pendingReview: Array.isArray(roles) && roles.includes(REVIEW_ROLE),
  hasShortSales: false,
  hasLongSales: false,
  netShortToday: false,
  unresolved: false,
});

export const toggleFilter = (filters, key) =>
  FILTER_KEYS.includes(key) ? { ...filters, [key]: filters?.[key] !== true } : filters;

export const activeMoreFilterCount = (filters) =>
  MORE_FILTERS.filter(([k]) => filters?.[k] === true).length;

const RESULT_LABELS = { flagged: "Flagged", clean: "Clean", unavailable: UNAVAILABLE };

// Status column: result, a Related chip only when related is not strictly clean, and markers minus "No".
// Same rule as the PreClear rail: an unresolved security is never a green all-clear.
const isUnresolvedRecord = (record) =>
  typeof record?.unresolved_security === "boolean"
    ? record.unresolved_security
    : record?.snapshot?.unresolved_security === true;

export const statusCell = (record) => {
  const verdict = verdictTone(record?.flagged);
  const tone = verdict === "clean" && isUnresolvedRecord(record) ? "neutral" : verdict;
  const related = record?.related_flagged;
  return {
    result: { label: RESULT_LABELS[verdict], tone },
    related:
      related === false
        ? null
        : related === true
          ? { label: "Related flagged", tone: "flagged" }
          : { label: `Related: ${UNAVAILABLE}`, tone: "unavailable" },
    markers: railMarkerBadges(record),
  };
};

export const DETAIL_SECTIONS = [
  "Reasons",
  "Markers",
  "Signature",
  "Attestation version",
  "Review history",
  "Verify",
];

// Static fields of an expanded row; review history and Verify are fetched when the row opens.
export const historyDetail = (record) => {
  const version = record?.attestation_version;
  return {
    reasons: reasonLines(record?.flag_reasons),
    markers: markerBadges(record),
    signature: typeof record?.signature === "string" && record.signature ? record.signature : UNAVAILABLE,
    attestationVersion: version === null || version === undefined || version === "" ? UNAVAILABLE : String(version),
  };
};

export const reviewHistoryLine = (rv) =>
  `${formatAttestedAt(rv?.reviewed_at)} ${String(rv?.decision)} by ${String(rv?.reviewer)}: ${String(rv?.note)}`;

// Verify runs by itself only the first time a row opens; afterwards only "Verify again" runs it.
export const shouldAutoVerify = ({ opening, autoVerified, verify }) =>
  opening === true &&
  autoVerified !== true &&
  !verify?.loading &&
  !verify?.result &&
  !verify?.error;

export const verifyLinkLabel = (verify) => {
  if (verify?.loading) return "Verifying...";
  if (verify?.result || verify?.error) return "Verify again";
  return "Verify";
};

// Review history of an expanded row: state is undefined, { status: "loading" | "failed" } or
// { status: "loaded", items }.
export const reviewHistoryView = (state) => {
  if (!state) return { kind: "none" };
  if (state.status === "loading") return { kind: "loading", text: "Loading review history..." };
  if (state.status === "loaded" && Array.isArray(state.items)) {
    return state.items.length === 0
      ? { kind: "empty", text: "No reviews yet" }
      : { kind: "list", items: state.items };
  }
  return { kind: "failed", text: "Review history could not be loaded", retry: true };
};

// The compliance default applies once, when roles are known, and never over a filter the user already set.
export const shouldApplyRoleDefault = ({ applied, touched, roles }) =>
  applied !== true && touched !== true && Array.isArray(roles);

export const pricingDateLine = (record) => {
  const date = record?.pricing_date;
  return `Pricing date ${typeof date === "string" && date ? date : UNAVAILABLE}`;
};

// The six History columns. The wide table renders these in order; the narrow card view renders
// CARD_LAYOUT, which must hold every key (missingCardFields) so cards never drop a table field.
export const HISTORY_FIELDS = [
  { key: "when", label: "When and trader" },
  { key: "security", label: "Security" },
  { key: "window", label: "Window" },
  { key: "status", label: "Status" },
  { key: "review", label: "Review" },
  { key: "actions", label: "Actions" },
];

// Card: security and when as the heading, labelled rows, then the actions line.
export const CARD_LAYOUT = {
  header: ["security", "when"],
  rows: ["window", "status", "review"],
  footer: ["actions"],
};

export const cardFieldKeys = (layout) => [
  ...(layout?.header || []),
  ...(layout?.rows || []),
  ...(layout?.footer || []),
];

export const missingCardFields = (layout) => {
  const shown = new Set(cardFieldKeys(layout));
  return HISTORY_FIELDS.map((f) => f.key).filter((key) => !shown.has(key));
};

export const fieldLabel = (key) => HISTORY_FIELDS.find((f) => f.key === key)?.label || "";

// The History table needs about this much width; narrower containers get stacked cards.
export const TABLE_MIN_WIDTH = 1000;
// Table only when a real container width is known and wide enough (unmeasured means cards, never a squeezed table).
export const showTableView = (width) => Number.isFinite(width) && width >= TABLE_MIN_WIDTH;
